import { useEffect, useState, useCallback } from "react";
import { useMediaPlayer } from "../lib/mediaPlayer";

// Cinematic first-visit intro. Sits above everything until the visitor
// EXPLICITLY interacts with it (click, tap, Enter, Space, or Esc). There is
// NO auto-dismiss timer — nothing happens until they press something.
//
// Music autoplay policy: the persistent player only kicks into playback on the
// FIRST load of a browser session. On subsequent reloads or route changes
// within the same session, the splash still greets the visitor but pressing
// ENTER just closes the splash — playback state (paused/playing) is left
// exactly as it was before. `sessionStorage` is the source of truth here so
// the flag naturally clears when the browser tab is closed → the next
// "visit" gets a fresh autoplay.
const SESSION_KEY = "syndafam-splash-autoplayed";

export default function WelcomeSplash() {
  const p = useMediaPlayer();
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  // Splash should appear on every fresh page load (first visit AND refresh),
  // then stay hidden during SPA navigation because this component only mounts
  // once at the App root. That gives visitors a consistent "enter the fam"
  // moment every time they open or reload the site, while never blocking
  // in-app route changes.
  useEffect(() => {
    setVisible(true);
  }, []);

  const dismiss = useCallback(() => {
    if (leaving) return;
    setLeaving(true);
    // Autoplay is a one-shot-per-visit gift. If this is the first splash
    // dismiss within the session, un-mute + start playback (the click itself
    // satisfies the browser's user-gesture requirement). On subsequent
    // reloads we simply close the splash and leave playback as-is.
    try {
      const alreadyAutoplayed = typeof window !== "undefined" && window.sessionStorage.getItem(SESSION_KEY) === "1";
      if (!alreadyAutoplayed) {
        p.setMuted(false);
        p.setPlaying(true);
        try { window.sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* private mode */ }
      }
    } catch { /* provider not ready */ }
    // Match the CSS fade-out (700ms) then unmount.
    setTimeout(() => setVisible(false), 750);
  }, [leaving, p]);

  // NOTE: No auto-dismiss timer. Splash stays until the visitor presses a
  // button, taps the backdrop, or hits a key. This matches the brand promise
  // of a deliberate "enter the fam" moment.

  // ESC / Enter / Space / arrow keys — treat any key as a "press to enter".
  useEffect(() => {
    if (!visible) return;
    const onKey = () => dismiss();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, dismiss]);

  if (!visible) return null;

  return (
    <div
      onClick={dismiss}
      role="dialog"
      aria-label="Welcome to SyndaFam"
      className={`fixed inset-0 z-[200] flex items-center justify-center overflow-hidden cursor-pointer transition-opacity duration-700 ${leaving ? "opacity-0" : "opacity-100"}`}
      style={{ background: "radial-gradient(circle at 50% 50%, #180708 0%, #050505 60%, #000 100%)" }}
      data-testid="welcome-splash"
    >
      {/* Ambient orbs */}
      <div className="orb orb-red orb-live pointer-events-none" style={{ width: 620, height: 620, top: "-150px", left: "-120px", opacity: 0.55 }} />
      <div className="orb orb-amber orb-live pointer-events-none" style={{ width: 520, height: 520, bottom: "-140px", right: "-100px", opacity: 0.5, animationDelay: "-4s" }} />
      <div className="orb orb-cyan orb-live pointer-events-none" style={{ width: 340, height: 340, top: "45%", left: "40%", opacity: 0.18, animationDelay: "-2s" }} />

      {/* Local keyframes */}
      <style>{`
        @keyframes splash-rise {
          0%   { opacity: 0; transform: translateY(24px) scale(0.96); filter: blur(12px); }
          70%  { opacity: 1; filter: blur(0); }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        @keyframes splash-slice {
          0% { transform: scaleX(0); }
          100% { transform: scaleX(1); }
        }
        @keyframes splash-blink { 0%, 60%, 100% { opacity: 1; } 30% { opacity: 0.15; } }
      `}</style>

      <div className="relative z-10 text-center px-6" style={{ maxWidth: 900 }}>
        <div
          className="font-mono text-[10px] sm:text-xs tracking-[0.6em] text-[#FCA311]"
          style={{ animation: "splash-rise 700ms ease-out 0ms both" }}
        >
          // WELCOME TO THE FAM
        </div>

        <h1
          className="font-display leading-[0.85] tracking-tighter mt-6"
          style={{ fontSize: "clamp(4rem, 14vw, 12rem)", animation: "splash-rise 850ms ease-out 220ms both" }}
        >
          <span className="text-white">SYNDA</span>
          <span className="text-[#E63946] text-glow-red">FAM</span>
          <span className="text-white">.</span>
        </h1>

        <div
          className="mx-auto mt-4 h-[3px] bg-gradient-to-r from-transparent via-[#E63946] to-transparent"
          style={{ width: "min(560px, 80vw)", transformOrigin: "center", animation: "splash-slice 900ms cubic-bezier(.6,.05,.28,.98) 420ms both" }}
        />

        <div
          className="mt-8 font-mono text-sm sm:text-base tracking-[0.35em] text-white/70"
          style={{ animation: "splash-rise 800ms ease-out 700ms both" }}
        >
          WE ARE THE WAVE
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); dismiss(); }}
          className="mt-12 inline-flex items-center gap-3 bg-[#E63946] hover:bg-[#C92A36] text-white font-mono text-xs tracking-[0.4em] px-8 py-4 shadow-[0_0_40px_rgba(230,57,70,0.55)] transition-all"
          style={{ animation: "splash-rise 900ms ease-out 1100ms both" }}
          data-testid="welcome-splash-enter"
          aria-label="Enter site"
        >
          ENTER
          <span style={{ animation: "splash-blink 1.4s ease-in-out infinite" }}>→</span>
        </button>

        <div
          className="mt-6 font-mono text-[9px] tracking-[0.4em] text-white/30"
          style={{ animation: "splash-rise 900ms ease-out 1600ms both" }}
        >
          TAP ANYWHERE OR PRESS ENTER
        </div>
      </div>
    </div>
  );
}
